import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import type { FccManualPoolingOffReason } from '@backoffice-frontend/graphql'
import { useTimers } from '@backoffice-frontend/hooks'
import {
  MoiaChip,
  useMoiaErrorNotification,
} from '@backoffice-frontend/patterns'
import { CheckCircleSolidIcon } from '@moia-dev/pace-icons'
import {
  Badge,
  Button,
  Dialog,
  LoadingIndicator,
  Switch,
  Text,
  useDialog,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdPoolingToggleVehicleFragment } from './AdPoolingToggle.hook'
import { useTogglePoolingMutation } from './AdPoolingToggle.hook'
import { getManualPoolingOffReasonTranslation } from './locales/mappings'
import { manualPoolingOffReasons } from './manualPoolingOffReasons'

type AdPoolingToggleProps = {
  vehicle: AdPoolingToggleVehicleFragment
  showTitle?: boolean
  onTurnedOff?: VoidFunction
}

export const AdPoolingToggle = ({
  vehicle,
  showTitle = true,
  onTurnedOff,
}: AdPoolingToggleProps) => {
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { t } = useTranslation()
  const [selectedPoolingOffReason, setSelectedPoolingOffReason] =
    useState<FccManualPoolingOffReason>()
  const [indicatedStatus, setIndicatedStatus] = useState<boolean | undefined>(
    undefined,
  )
  const { setTimer, clearTimer } = useTimers()

  // zIndex above mui modals
  const reasonDialogZIndex = 1500
  const {
    id: reasonDialogId,
    isOpen: isReasonDialogOpen,
    closeDialog: closeReasonDialog,
    openDialog: openReasonDialog,
  } = useDialog(() => {}, reasonDialogZIndex)

  const isPoolingEnabled = vehicle.pooling?.enabled ?? false
  const [togglePooling, { loading: mutationLoading }] =
    useErrorHandlingMutation(useTogglePoolingMutation, {
      onError: () => {
        enqueueMoiaErrorNotification(
          t('adFleetMap_toast_poolingStateFailed_label'),
        )
        setIndicatedStatus(undefined)
      },
      onCompleted: data => {
        const { fccUpdateAdPoolingStatus: response } = data
        if (response.__typename === 'FccUpdateAdPoolingStatusFailure') {
          setIndicatedStatus(undefined)
          enqueueMoiaErrorNotification(
            t('adFleetMap_toast_poolingStateFailed_label'),
          )
        } else {
          setSelectedPoolingOffReason(undefined)
          // It takes a few seconds for the toggled pooling status to take effect.
          // Keep toggle in target state for a bit to not switch back:
          setTimer(
            'resetIndicatedStatus',
            () => setIndicatedStatus(undefined),
            8000,
          )
        }
      },
    })

  const turnPoolingOn = () => {
    clearTimer('resetIndicatedStatus')
    setIndicatedStatus(true)
    void togglePooling({
      variables: {
        vehicleId: vehicle.id,
        poolingEnabled: true,
      },
    })
  }

  const turnPoolingOff = () => {
    clearTimer('resetIndicatedStatus')
    setIndicatedStatus(false)
    void togglePooling({
      variables: {
        vehicleId: vehicle.id,
        poolingEnabled: false,
        manualPoolingOffReason: selectedPoolingOffReason,
      },
    })
  }

  const handleCloseDialog = () => {
    setSelectedPoolingOffReason(undefined)
    closeReasonDialog()
  }

  const isPoolingStatePending =
    mutationLoading ||
    (indicatedStatus !== undefined && indicatedStatus !== isPoolingEnabled)

  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.space.Space2,
      })}
    >
      {showTitle && (
        <>
          {isPoolingStatePending ? (
            <LoadingIndicator size={20} />
          ) : (
            <Text color="reduced">{t('common_pooling_label')}</Text>
          )}
        </>
      )}
      {!showTitle && (
        <Badge
          variant={isPoolingEnabled ? 'success' : 'warning'}
          label={
            isPoolingEnabled
              ? t('common_poolingOn_label')
              : t('common_poolingOff_label')
          }
          icon={<CheckCircleSolidIcon />}
        />
      )}

      <Switch
        checked={
          indicatedStatus !== undefined ? indicatedStatus : isPoolingEnabled
        }
        onChange={checked => {
          if (checked) {
            turnPoolingOn()
          } else {
            openReasonDialog()
          }
        }}
        disabled={isPoolingStatePending}
      />

      {isReasonDialogOpen && (
        <Dialog
          onClose={handleCloseDialog}
          actions={
            <>
              <Button
                label={t('common_cancel_button')}
                variant="secondary"
                onClick={handleCloseDialog}
              />
              <Button
                label={t('adFleetMap_dialog_poolingOffDialogConfirm_button')}
                variant="primary"
                disabled={selectedPoolingOffReason === undefined}
                onClick={() => {
                  turnPoolingOff()
                  closeReasonDialog()
                  onTurnedOff?.()
                }}
              />
            </>
          }
          title={t('common_selectReason_label')}
          id={reasonDialogId}
          css={{ minWidth: 480 }}
        >
          <div
            css={theme => ({
              display: 'flex',
              gap: theme.spacing(1),
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            })}
          >
            {manualPoolingOffReasons.map(reason => (
              <MoiaChip
                key={reason}
                color="primary"
                label={getManualPoolingOffReasonTranslation(t, reason)}
                selected={selectedPoolingOffReason === reason}
                onClick={() => setSelectedPoolingOffReason(reason)}
              />
            ))}
          </div>
        </Dialog>
      )}
    </div>
  )
}
